import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BehaviorSubject, Subject } from 'rxjs';

export interface RecordingState {
  isRecording: boolean;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  recordButtonText: string;
  recordButtonDisabled: boolean;
  countdown: number;
}

export interface CommentRecording {
  blob: Blob;
  url: string;
  duration: number;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root',
})
export class VideoRecoderService {
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private videoStream: MediaStream | null = null;
  private audioStream: MediaStream | null = null;
  private combinedStream: MediaStream | null = null;

  private chunkBuffer: Blob[] = [];
  private chunkInterval: any = null;
  private readonly CHUNK_INTERVAL_MS = 5000;
  public currentChunkIndex$ = new BehaviorSubject<number>(0);

  private totalRecordedDuration: number = 0;

  public commentChunkReady$ = new Subject<Blob>();
  private commentChunkBuffer: Blob[] = [];
  private commentChunkInterval: any = null;

  private commentChunks: Blob[] = [];
  private commentRecorder: MediaRecorder | null = null;
  private commentStartTime: number = 0;
  private commentTimer: any = null;
  private commentRecordingStartTime: number = 0;
  private MAX_COMMENT_DURATION = 30;
  private readonly MAX_COMMENTS_PER_SESSION = 3;

  private recordingSessionId: number = 0;
  private pendingChunkFlushReason: string | null = null;
  private pausePending = false;

  public isRecording$ = new BehaviorSubject<boolean>(false);
  public recordedBlob$ = new Subject<Blob | null>();
  public previewStream$ = new BehaviorSubject<MediaStream | null>(null);
  public isVideoOn$ = new BehaviorSubject<boolean>(false);
  public isMicOn$ = new BehaviorSubject<boolean>(false);
  private isCancelled = false;

  public chunkReady$ = new Subject<Blob>();
  public recordingStartTime: number = 0;

  public volumeLevel$ = new BehaviorSubject<number>(0);
  public commentVolumeLevel$ = new BehaviorSubject<number>(0);

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private animationFrameId: number | null = null;

  private commentAudioContext: AudioContext | null = null;
  private commentAnalyser: AnalyserNode | null = null;
  private commentAnimationFrameId: number | null = null;

  public isCommentRecording$ = new BehaviorSubject<boolean>(false);
  public commentTimeRemaining$ = new BehaviorSubject<number>(
    this.MAX_COMMENT_DURATION
  );
  public commentsUsed$ = new BehaviorSubject<number>(0);
  public recordedComment$ = new Subject<CommentRecording | null>();
  public commentsRemaining$ = new BehaviorSubject<number>(
    this.MAX_COMMENTS_PER_SESSION
  );

  constructor(private sanitizer: DomSanitizer) {}

  private flushChunkBuffer(reason: string, buffer: Blob[] = this.chunkBuffer) {
    if (buffer.length === 0) {
      return;
    }

    const chunkBlob = new Blob(buffer, { type: 'video/webm' });
    const newIndex = this.currentChunkIndex$.value + 1;
    this.currentChunkIndex$.next(newIndex);

    this.chunkReady$.next(chunkBlob);

    buffer.length = 0;
  }

  private flushCommentChunkBuffer(reason: string) {
    if (this.commentChunkBuffer.length === 0) {
      return;
    }

    const chunkBlob = new Blob(this.commentChunkBuffer, { type: 'video/webm' });

    this.commentChunkReady$.next(chunkBlob);

    this.commentChunkBuffer = [];
  }

  private finalizePauseState() {
    if (!this.pausePending) {
      return;
    }

    if (this.recordingStartTime > 0) {
      const segmentDuration = Date.now() - this.recordingStartTime;
      this.totalRecordedDuration += segmentDuration;
    }

    this.recordingStartTime = 0;
    this.pausePending = false;

    this.isRecording$.next(false);

    if (this.chunkInterval) {
      clearInterval(this.chunkInterval);
      this.chunkInterval = null;
    }

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.volumeLevel$.next(0);
  }

  async toggleVideoStream() {
    if (this.videoStream) {
      this.stopStream(this.videoStream);
      this.videoStream = null;
      this.previewStream$.next(null);
      this.isVideoOn$.next(false);
    } else {
      try {
        this.videoStream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        this.previewStream$.next(this.videoStream);
        this.isVideoOn$.next(true);
      } catch (error) {
      }
    }
  }

  async toggleAudioStream() {
    if (this.audioStream) {
      this.stopStream(this.audioStream);
      this.audioStream = null;
      this.isMicOn$.next(false);
    } else {
      try {
        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        this.isMicOn$.next(true);
      } catch (error) {
      }
    }
  }

  closeMicAndCamera() {
    this.stopStream(this.audioStream);
    this.stopStream(this.videoStream);
    this.videoStream = null;
    this.audioStream = null;
    this.isMicOn$.next(false);
    this.isVideoOn$.next(false);
  }

  monitorAudio(stream: MediaStream) {
    if (this.audioContext) {
      this.audioContext.close();
    }

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    source.connect(this.analyser);

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateVolume = () => {
      if (!this.analyser || !this.isRecording$.value) {
        this.volumeLevel$.next(0);
        this.animationFrameId = null;
        return;
      }

      this.analyser.getByteFrequencyData(dataArray);

      let values = 0;
      for (let i = 0; i < bufferLength; i++) {
        values += dataArray[i];
      }
      const average = values / bufferLength;
      const volumePercentage = Math.round((average / 128) * 100);

      this.volumeLevel$.next(volumePercentage);
      this.animationFrameId = requestAnimationFrame(updateVolume);
    };

    updateVolume();
  }

  private monitorCommentAudio(stream: MediaStream) {
    if (this.commentAudioContext) {
      this.commentAudioContext.close();
    }

    if (this.commentAnimationFrameId !== null) {
      cancelAnimationFrame(this.commentAnimationFrameId);
      this.commentAnimationFrameId = null;
    }

    this.commentAudioContext = new AudioContext();
    const source = this.commentAudioContext.createMediaStreamSource(stream);
    this.commentAnalyser = this.commentAudioContext.createAnalyser();
    this.commentAnalyser.fftSize = 256;
    source.connect(this.commentAnalyser);

    const bufferLength = this.commentAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateVolume = () => {
      if (!this.commentAnalyser || !this.isCommentRecording$.value) {
        this.commentVolumeLevel$.next(0);
        this.commentAnimationFrameId = null;
        return;
      }

      this.commentAnalyser.getByteFrequencyData(dataArray);

      let values = 0;
      for (let i = 0; i < bufferLength; i++) {
        values += dataArray[i];
      }
      const average = values / bufferLength;
      const volumePercentage = Math.round((average / 128) * 100);

      this.commentVolumeLevel$.next(volumePercentage);
      this.commentAnimationFrameId = requestAnimationFrame(updateVolume);
    };

    updateVolume();
  }

  startRecording() {
    if (!this.videoStream || !this.audioStream) {
      return;
    }

    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.pausePending = false;
      this.pendingChunkFlushReason = null;
      this.recordingStartTime = Date.now();
      this.isRecording$.next(true);
      this.monitorAudio(this.audioStream);
      this.mediaRecorder.resume();
      this.startChunkInterval(this.recordingSessionId, this.chunkBuffer);
      return;
    }

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      return;
    }

    try {
      this.combinedStream = new MediaStream([
        ...this.videoStream.getTracks(),
        ...this.audioStream.getTracks(),
      ]);

      const options = { mimeType: 'video/webm;codecs=vp9,opus' };
      this.mediaRecorder = new MediaRecorder(this.combinedStream, options);

      this.recordingStartTime = Date.now();
      this.recordingSessionId += 1;
      const sessionId = this.recordingSessionId;
      const sessionChunkBuffer: Blob[] = [];
      this.chunkBuffer = sessionChunkBuffer;

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data);
          sessionChunkBuffer.push(event.data);
        }

        if (this.pausePending && this.mediaRecorder?.state === 'paused') {
          const reason = this.pendingChunkFlushReason ?? 'pause';
          this.pendingChunkFlushReason = null;
          this.flushChunkBuffer(reason, sessionChunkBuffer);
          const combinedBlob = new Blob(this.chunks, { type: 'video/webm' });
          this.recordedBlob$.next(combinedBlob);
          this.finalizePauseState();
        }
      };

      this.mediaRecorder.onstop = () => {
        const sessionDuration = this.recordingStartTime > 0
          ? Date.now() - this.recordingStartTime
          : 0;

        this.flushChunkBuffer('stop', sessionChunkBuffer);

        const isCurrentSession = sessionId === this.recordingSessionId;

        if (sessionDuration > 0) {
          this.totalRecordedDuration += sessionDuration;
        }

        this.recordingStartTime = 0;
        this.pausePending = false;
        this.pendingChunkFlushReason = null;

        const combinedBlob = new Blob(this.chunks, { type: 'video/webm' });
        this.recordedBlob$.next(combinedBlob);

        if (isCurrentSession) {
          this.isRecording$.next(false);

          if (this.chunkInterval) {
            clearInterval(this.chunkInterval);
            this.chunkInterval = null;
          }

          if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
          }
          if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
          }
          this.volumeLevel$.next(0);
        }
      };

      this.isRecording$.next(true);
      this.monitorAudio(this.audioStream);

      this.mediaRecorder.start(100);

      this.startChunkInterval(sessionId, sessionChunkBuffer);
    } catch (error) {
    }
  }

  private startChunkInterval(sessionId: number, buffer: Blob[]) {
    if (this.chunkInterval) {
      clearInterval(this.chunkInterval);
      this.chunkInterval = null;
    }

    this.chunkInterval = setInterval(() => {
      if (buffer.length > 0 && this.isRecording$.value && sessionId === this.recordingSessionId) {
        this.flushChunkBuffer('interval', buffer);
      }
    }, this.CHUNK_INTERVAL_MS);
  }

  pauseRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.pausePending = true;
      this.pendingChunkFlushReason = 'pause';
      this.mediaRecorder.pause();
      this.mediaRecorder.requestData();
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.pausePending = false;
      this.pendingChunkFlushReason = null;
      this.mediaRecorder.stop();
    }
  }

  clearRecording() {
    this.chunks = [];
    this.chunkBuffer = [];
    this.currentChunkIndex$.next(0);
    this.recordedBlob$.next(null);
  }

  getTotalRecordedDuration(): number {
    let total = this.totalRecordedDuration;

    if (this.isRecording$.value && this.recordingStartTime > 0) {
      total += (Date.now() - this.recordingStartTime);
    }

    return total;
  }

  resetTotalDuration() {
    this.totalRecordedDuration = 0;
    this.recordingStartTime = 0;
    this.chunks = [];
    this.chunkBuffer = [];
    this.currentChunkIndex$.next(0);
  }

  finalizeAndCleanup() {
    this.stopRecording();
    this.closeMicAndCamera();
  }

  getCameraSettings() {
    if (this.videoStream) {
      const videoTrack = this.videoStream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      return {
        width: settings.width,
        height: settings.height,
        frameRate: settings.frameRate,
        label: videoTrack.label,
      };
    }
    return null;
  }

  cancelRecording() {
    this.isCancelled = true;

    if (this.chunkInterval) {
      clearInterval(this.chunkInterval);
      this.chunkInterval = null;
    }

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    this.isRecording$.next(false);
    this.volumeLevel$.next(0);
    this.stopStream(this.videoStream);
    this.stopStream(this.audioStream);
    this.stopStream(this.combinedStream);
    this.videoStream = null;
    this.audioStream = null;
    this.combinedStream = null;
    this.previewStream$.next(null);
    this.isVideoOn$.next(false);
    this.isMicOn$.next(false);

    this.chunks = [];
    this.chunkBuffer = [];
    this.currentChunkIndex$.next(0);
    this.recordedBlob$.next(null);
  }

  async startCommentRecording() {
    if (this.commentsUsed$.value >= this.MAX_COMMENTS_PER_SESSION) {
      return;
    }

    try {
      if (!this.videoStream || !this.audioStream) {
        return;
      }

      this.commentChunks = [];
      this.commentChunkBuffer = [];

      const commentStream = new MediaStream([
        ...this.videoStream.getTracks(),
        ...this.audioStream.getTracks(),
      ]);

      const options = { mimeType: 'video/webm;codecs=vp9,opus' };
      this.commentRecorder = new MediaRecorder(commentStream, options);

      this.commentRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.commentChunks.push(event.data);
          this.commentChunkBuffer.push(event.data);
        }
      };

      this.commentRecorder.onstop = () => {
        const elapsedTime = (Date.now() - this.commentStartTime) / 1000;

        const blob = new Blob(this.commentChunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);

        const comment: CommentRecording = {
          blob,
          url,
          duration: elapsedTime,
          timestamp: new Date(),
        };

        this.recordedComment$.next(comment);

        this.flushCommentChunkBuffer('stop');

        this.isCommentRecording$.next(false);
        this.commentTimeRemaining$.next(this.MAX_COMMENT_DURATION);

        if (this.commentChunkInterval) {
          clearInterval(this.commentChunkInterval);
          this.commentChunkInterval = null;
        }

        if (this.commentAnimationFrameId !== null) {
          cancelAnimationFrame(this.commentAnimationFrameId);
          this.commentAnimationFrameId = null;
        }
        if (this.commentAudioContext) {
          this.commentAudioContext.close();
          this.commentAudioContext = null;
        }
        this.commentVolumeLevel$.next(0);

        if (this.commentTimer) {
          clearInterval(this.commentTimer);
          this.commentTimer = null;
        }
      };

      this.commentStartTime = Date.now();
      this.commentRecordingStartTime = Date.now();

      this.commentRecorder.start(100);

      this.isCommentRecording$.next(true);

      this.monitorCommentAudio(this.audioStream);

      this.startCommentChunkInterval();

      let secondsRemaining = this.MAX_COMMENT_DURATION;
      this.commentTimeRemaining$.next(secondsRemaining);

      this.commentTimer = setInterval(() => {
        secondsRemaining--;
        this.commentTimeRemaining$.next(secondsRemaining);

        if (secondsRemaining <= 0) {
          this.stopCommentRecording();
        }
      }, 1000);
    } catch (error) {
    }
  }

  private startCommentChunkInterval() {
    this.commentChunkInterval = setInterval(() => {
      if (this.commentChunkBuffer.length > 0 && this.isCommentRecording$.value) {
        this.flushCommentChunkBuffer('interval');
      }
    }, this.CHUNK_INTERVAL_MS);
  }

  stopCommentRecording() {
    if (this.commentRecorder && this.commentRecorder.state !== 'inactive') {
      if (this.commentRecorder.state === 'recording') {
        this.commentRecorder.requestData();
      }
      this.commentRecorder.stop();
    }

    if (this.commentTimer) {
      clearInterval(this.commentTimer);
      this.commentTimer = null;
    }

    if (this.commentChunkInterval) {
      clearInterval(this.commentChunkInterval);
      this.commentChunkInterval = null;
    }
  }

  cancelCommentRecording() {
    if (this.commentRecorder && this.commentRecorder.state !== 'inactive') {
      this.commentRecorder.stop();
    }

    if (this.commentTimer) {
      clearInterval(this.commentTimer);
      this.commentTimer = null;
    }

    if (this.commentChunkInterval) {
      clearInterval(this.commentChunkInterval);
      this.commentChunkInterval = null;
    }

    if (this.commentAnimationFrameId !== null) {
      cancelAnimationFrame(this.commentAnimationFrameId);
      this.commentAnimationFrameId = null;
    }
    if (this.commentAudioContext) {
      this.commentAudioContext.close();
      this.commentAudioContext = null;
    }

    this.commentChunks = [];
    this.commentChunkBuffer = [];
    this.isCommentRecording$.next(false);
    this.commentTimeRemaining$.next(this.MAX_COMMENT_DURATION);
    this.commentVolumeLevel$.next(0);
  }

  resetCommentSession() {
    this.commentsUsed$.next(0);
    this.commentsRemaining$.next(this.MAX_COMMENTS_PER_SESSION);
    this.resetMaxCommentDuration();
  }

  getMaxCommentDuration(): number {
    return this.MAX_COMMENT_DURATION;
  }

  setMaxCommentDuration(durationSec: number) {
    this.MAX_COMMENT_DURATION = durationSec;
    this.commentTimeRemaining$.next(durationSec);
  }

  resetMaxCommentDuration() {
    this.MAX_COMMENT_DURATION = 30;
    this.commentTimeRemaining$.next(30);
  }

  getMaxCommentsPerSession(): number {
    return this.MAX_COMMENTS_PER_SESSION;
  }

  getCurrentRecordingDuration(): number {
    if (!this.isRecording$.value || this.recordingStartTime === 0) {
      return 0;
    }
    return Date.now() - this.recordingStartTime;
  }

  getCurrentCommentRecordingDuration(): number {
    if (!this.isCommentRecording$.value || this.commentRecordingStartTime === 0) {
      return 0;
    }
    return Date.now() - this.commentRecordingStartTime;
  }

  private stopStream(stream: MediaStream | null) {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }
}
